<template>
  <div id="app">
    <h2>デッキ</h2>
    <deck-editor v-model="deck"></deck-editor>
    <button class="clear" @click="clearDeck">クリア</button>
    <p class="info">ENTER キーで行追加</p>
    <h2>組み合わせ</h2>
    <combinations-editor v-model="combinations"></combinations-editor>
    <button class="clear" @click="clearCombinations">クリア</button>
    <p class="info">ENTER キーで行追加<br>SHIFT + ENTER キーで列追加</p>
    <h2>確率</h2>
    <label>手札の枚数：<input type="number" v-model.number="hand"></label>
    <div class="probability">
      <p>手札の組み合わせの総数：{{totalCombinations}} 通り</p>
      <p v-for="(prob, i) in matches" :key="i">【{{prob.pattern}}】 にマッチする組み合わせ：{{prob.combinations}} 通り（{{prob.probability}} ％）</p>
      <p>マッチした組み合わせの総数：{{totalMatches}} 通り</p>
      <p>確率：{{totalProbability}} ％（リスタート：{{totalRestartProbability}} ％）</p>
    </div>
    <!-- <label><input type="checkbox" v-model="restart">スキル：リスタート</label> -->
    <button @click="calc">計算する</button>
  </div>
</template>

<script>
import DeckEditor from './components/DeckEditor.vue'
import CombinationsEditor from './components/CombinationsEditor.vue'
import { Base64 } from 'js-base64'
import Serializer from './js/urlserializer.js'
import CalculatorWorker from 'worker-loader!./js/calculator-worker.js'

// 'A, B' 形式の組み合わせ要素を Array に変換
function _processORCombination(comb) {
  return comb.map(i => {
    if (i.match(/[,、，]/)) return i.split(/\s*[,、，]\s*/)
    return i
  })
}

// CombinationsEditor の value を YugiohProbability.js 用に変換する
function modifyCombinations(combs) {
  return combs.filter(comb => {
    return comb.some(e => e !== '')
  }).map(comb => _processORCombination(comb))
}

// DeckEditor の value を YugiohProbability.js 用に変換する
function modifyDeck(deck) {
  return deck.filter(card => card.num > 0) // 0 枚のカードを除外する
  // カード名の両端の空白を取り除く
  .map(card => {
    return {
      num: card.num,
      name: card.name.trim(),
    }
  })
  // フラット化する
  .reduce((acc, card) =>
    [...acc, ...new Array(card.num).fill(card.name)]
  , [])
}

export default {
  name: 'app',
  data() {
    return {
      deck: [{name: '', num: 0}],
      combinations: [['']],
      hand: 4,
      restart: false,

      totalCombinations: 0,
      totalMatches: 0,
      matches: [],

      calculator: null,
    }
  },
  computed: {
    totalProbability() {
      if (this.totalCombinations === 0) return 0
      return (this.totalMatches / this.totalCombinations * 100).toFixed(4)
    },
    totalRestartProbability() {
      if (this.totalCombinations === 0) return 0
      let prob = this.totalMatches / this.totalCombinations
      return ((prob + (1 - prob) * prob) * 100).toFixed(4)
    },
  },
  mounted() {
    let flag = false

    let {searchParams} = new URL(document.location)
    // 通常版
    let deck = searchParams.get('deck')
    let comb = searchParams.get('comb')
    if (deck !== null) {
      this.deck = JSON.parse(Base64.decode(deck))
      flag = true
    }
    if (comb !== null) {
      this.combinations = JSON.parse(Base64.decode(comb))
      flag = true
    }
    // 圧縮版
    let d = searchParams.get('d')
    let c = searchParams.get('c')
    this.hand = parseInt(searchParams.get('h')) || 4
    if (d !== null) {
      this.deck = Serializer.deserializeDeck(d)
      flag = true
    }
    if (c !== null) {
      this.combinations = Serializer.deserializeCombinations(c)
      flag = true
    }

    if (flag) this.calc()
  },
  methods: {
    clearDeck() {
      this.deck = [{name: '', num: 0}]
    },
    clearCombinations() {
      this.combinations = [['']]
    },
    calc() {
      let combinations = modifyCombinations(this.combinations)
      let deck = modifyDeck(this.deck)

      if (deck.length === 0 ||
          combinations.length === 0 ||
          deck.length < this.hand) {
        this.totalCombinations = 0
        this.totalMatches = 0
        this.matches = []
        return
      }

      if (this.calculator !== null) this.calculator.terminate()
      this.calculator = new CalculatorWorker()
      this.calculator.addEventListener('message', e => {
        let { result } = e.data
        let { totalCombinations, totalMatches, matches } = result

        this.totalCombinations = totalCombinations
        this.totalMatches = totalMatches
        this.matches = combinations.map(comb => {
          return comb.map(e => {
            if (Array.isArray(e)) return `[${e.join(' or ')}]`
            if (typeof e === 'string') return e
            if (typeof e === 'object') {
              // MEMO: Duplicate Combination は仕様変更により不要となったが、後方互換性のため表示のみ残す
              if (e.hasOwnProperty('name') && e.hasOwnProperty('num'))
                return new Array(e.num).fill(e.name).join(' + ')
            }
          }).join(' + ')
        }).map((pt, i) => {
          let comb = matches[i]
          return {
            pattern: pt,
            combinations: comb,
            probability: (comb / totalCombinations * 100).toFixed(4),
          }
        })
      }, false)
      this.calculator.postMessage({ deck, combinations, hand: this.hand })

      let url = new URL(document.location)
      url.searchParams.delete('deck')
      url.searchParams.delete('comb')
      url.searchParams.set('d', Serializer.serializeDeck(this.deck))
      url.searchParams.set('c', Serializer.serializeCombinations(this.combinations))
      url.searchParams.set('h', this.hand)
      window.history.pushState(null, null, url.toString())
      window.gtag('config', 'UA-137503442-1', {'page_path': url.pathname+url.search})
    },
  },
  components: {
    DeckEditor, CombinationsEditor,
  }
}
</script>
<style lang="stylus">
#app
  width 800px
  margin 0 auto
  font-family 'Avenir', Helvetica, Arial, sans-serif
  -webkit-font-smoothing antialiased
  -moz-osx-font-smoothing grayscale
  text-align center
  color #2c3e50
  .probability
    display flex
    flex-direction column
    p
      margin 5px 0
  label
    display block
  .info
    font-size 0.8em
    color #999
    margin 10px 0 0 0
  .clear
    margin-top 15px
@media (max-width: 767px)
  #app
    width 100%
  .info
    display none
</style>
